import type { ReactNode } from 'react';
import type { useExperimentStats } from '@/state/experiments/useExperimentStats';
import type { StackedField } from '../../../ConsolePanel';
import { cn } from 'helpers/cn';
import { GRID_TABLE_ROW, GRID_TABLE_ROWGROUP, GridTable, StackedRow } from '../../../ConsolePanel';

type SecondaryObjectivesProps = {
  manager: ReturnType<typeof useExperimentStats>;
};

const formatPercent = (value: number): string => `${value.toFixed(2)}%`;

const formatLift = (value: number): string => `${value > 0 ? '+' : ''}${value.toFixed(2)}%`;

/** Variant name takes the spare width; every reading column keeps a floor. */
const TEMPLATE = 'minmax(180px,1fr) repeat(4, minmax(100px,auto)) minmax(180px,auto)';

type CellProps = { children: ReactNode; className?: string; title?: string };

const Cell = ({ children, className, title }: CellProps) => (
  <div role="cell" title={title} className={cn('num text-foreground', className)}>
    {children}
  </div>
);

const Head = ({ children, className }: CellProps) => (
  <div role="columnheader" className={cn('micro-label', className)}>
    {children}
  </div>
);

/**
 * Supporting evidence, presented so it cannot be mistaken for a verdict.
 *
 * No p-value and no significance column: secondaries sit outside the primary's correction
 * family, and a per-secondary verdict is how a losing experiment gets reported as a win on
 * whichever supporting metric happened to move. The interval is the honest summary — one
 * that spans zero says "this moved no more than noise would".
 */
export const SecondaryObjectives = ({ manager }: SecondaryObjectivesProps) => {
  const { secondaryObjectives, secondaryStats, currency } = manager;

  // Currency is chosen by an effect once the payload arrives, so for one render there is
  // no scope to read. Saying "nothing recorded yet" during it would be a false statement
  // about data that is present.
  if (currency === null) return null;

  if (secondaryObjectives.length === 0) {
    return (
      <p className="supporting">
        No secondary objectives are selected for this experiment. Add them in the experiment&apos;s settings to see
        supporting metrics here.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {secondaryObjectives.map((event) => {
        const variants = secondaryStats?.[event];
        const rows = variants ? Object.entries(variants) : [];
        const hasData = rows.some(([, row]) => row.conversions > 0);

        /**
         * Derived once, rendered twice — as a grid row on a desktop and as a stack of
         * label/value pairs on a phone. Two renderers reading one array is what keeps
         * the narrow shape from drifting away from the wide one.
         */
        const readings = rows.map(([variantId, row]) => {
          // An interval that contains zero is indistinguishable from no effect,
          // so the lift is not emphasised in that case.
          const isConclusive = row.liftInterval ? row.liftInterval.lower > 0 || row.liftInterval.upper < 0 : false;

          return {
            variantId,
            row,
            name: row.variantName ?? `Variant ${variantId}`,
            fields: [
              { key: 'sessions', label: 'Sessions', value: row.sessions.toLocaleString() },
              { key: 'events', label: 'Events', value: row.conversions.toLocaleString() },
              { key: 'rate', label: 'Rate', value: formatPercent(row.rate) },
              {
                key: 'lift',
                label: 'Lift',
                value: row.lift === null ? '—' : formatLift(row.lift),
                className: cn(
                  row.lift === null && 'text-dim',
                  isConclusive && row.lift !== null && (row.lift > 0 ? 'text-ok' : 'text-danger'),
                ),
              },
              {
                key: 'interval',
                label: '95% interval on lift',
                value: row.liftInterval
                  ? `${formatLift(row.liftInterval.lower)} to ${formatLift(row.liftInterval.upper)}`
                  : '—',
                className: 'text-dim',
              },
            ] satisfies StackedField[],
          };
        });

        return (
          <div key={event} className="border-line bg-panel2 rounded-card border">
            <div className="border-line flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b px-4 py-2.5">
              <h4 className="text-foreground text-body font-medium">{event}</h4>
              {!hasData && <span className="supporting">collecting — nothing recorded yet</span>}
            </div>

            {rows.length === 0 ? (
              <p className="supporting px-4 py-3">
                {/*
                  A GA4 site only starts sending a newly mapped event once its runtime file
                  has been regenerated, so "no rows at all" is a real and temporary state
                  rather than a bug. Pertento-tracked sites report every event already, so
                  their history appears as soon as the mapping is saved.
                */}
                Waiting for the first events. On GA4 websites this begins after the tracking script next updates.
              </p>
            ) : (
              <>
                {/* Below `md` the same readings stack: the grid's floor is 760px, so a
                    phone would have to swipe the variant name off screen to reach the
                    lift, and a lift with no variant beside it is not a reading. */}
                <div className="flex flex-col gap-2 p-3 md:hidden">
                  {readings.map(({ variantId, row, name, fields }) => (
                    <StackedRow
                      key={variantId}
                      heading={
                        <div className="flex min-w-0 items-baseline gap-2">
                          <span className="text-foreground truncate text-body" title={name}>
                            {name}
                          </span>
                          {row.isOriginal && <span className="micro-label shrink-0">control</span>}
                        </div>
                      }
                      fields={fields}
                    />
                  ))}
                </div>

                <GridTable label={event} minWidth={760} template={TEMPLATE}>
                  <div role="row" className={cn(GRID_TABLE_ROW, 'border-line items-center border-b py-2')}>
                    <Head>Variant</Head>
                    <Head className="text-right">Sessions</Head>
                    <Head className="text-right">Events</Head>
                    <Head className="text-right">Rate</Head>
                    <Head className="text-right">Lift</Head>
                    <Head className="text-right">95% interval on lift</Head>
                  </div>

                  <div role="rowgroup" className={GRID_TABLE_ROWGROUP}>
                    {readings.map(({ variantId, row, name, fields }) => (
                      <div
                        key={variantId}
                        role="row"
                        className={cn(
                          GRID_TABLE_ROW,
                          'border-line hover:bg-hover items-center border-b py-2 transition-colors duration-150 last:border-b-0',
                        )}
                      >
                        <Cell className="truncate font-sans text-body" title={name}>
                          {name}
                          {row.isOriginal && <span className="micro-label ml-2">control</span>}
                        </Cell>
                        {fields.map((field) => (
                          <Cell key={field.key} className={cn('text-right', field.className)}>
                            {field.value}
                          </Cell>
                        ))}
                      </div>
                    ))}
                  </div>
                </GridTable>
              </>
            )}
          </div>
        );
      })}

      <p className="supporting">
        Secondary objectives are not corrected for multiple comparisons and carry no significance verdict. Read them as
        support for the primary result, not as results in their own right.
      </p>
    </div>
  );
};
